import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from 'components/ui/button';

export const AppShowcase = () => {
    const { t } = useTranslation();
    const screenshots = [
        {
            src: "/app-screenshots/uploaded_image_1_1764111109497.png",
            alt: t('appShowcase.step1.alt'),
            description: t('appShowcase.step1.desc')
        },
        {
            src: "/app-screenshots/uploaded_image_4_1764111109497.png",
            alt: t('appShowcase.step2.alt'),
            description: t('appShowcase.step2.desc')
        },
        {
            src: "/app-screenshots/uploaded_image_0_1764111109497.png",
            alt: t('appShowcase.step3.alt'),
            description: t('appShowcase.step3.desc')
        },
        {
            src: "/app-screenshots/uploaded_image_2_1764111109497.png",
            alt: t('appShowcase.step4.alt'),
            description: t('appShowcase.step4.desc')
        }
    ];

    const [currentIndex, setCurrentIndex] = useState(0);

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % screenshots.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    };

    // Auto-advance carousel - resets when currentIndex changes
    useEffect(() => {
        const timer = setInterval(nextSlide, 5000);
        return () => clearInterval(timer);
    }, [currentIndex]);

    return (
        <section id="how-it-works" className="py-20 bg-cyan-900 text-white overflow-hidden">
            <div className="container mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="order-2 lg:order-1">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            {t('appShowcase.title')}
                        </h2>
                        <p className="text-xl text-cyan-100 mb-8 leading-relaxed">
                            {t('appShowcase.description')}
                        </p>

                        <div className="space-y-6">
                            {screenshots.map((step, index) => (
                                <div
                                    key={index}
                                    className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 cursor-pointer
                    ${index === currentIndex ? 'bg-white/10 border border-white/20' : 'hover:bg-white/5 border border-transparent'}`}
                                    onClick={() => setCurrentIndex(index)}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors
                    ${index === currentIndex ? 'bg-primary text-white' : 'bg-white/10 text-cyan-200'}`}>
                                        {index + 1}
                                    </div>
                                    <div>
                                        <h3 className={`font-bold ${index === currentIndex ? 'text-white' : 'text-cyan-200'}`}>
                                            {step.alt}
                                        </h3>
                                        <p className="text-sm text-cyan-300 hidden md:block">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="order-1 lg:order-2 flex flex-col items-center relative">
                        {/* Phone Frame */}
                        <div className="relative w-[280px] sm:w-[320px]">
                            <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-2xl z-10"></div>
                                <div className="relative overflow-hidden rounded-[2.25rem] bg-white aspect-[320/693]">
                                    {screenshots.map((screenshot, index) => (
                                        <div
                                            key={index}
                                            className={`absolute inset-0 transition-opacity duration-500 ease-in-out
                      ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
                                        >
                                            <img
                                                src={screenshot.src}
                                                alt={screenshot.alt}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-primary/20 rounded-full blur-3xl -z-10 animate-pulse"></div>

                        {/* Navigation Buttons (Mobile only) */}
                        <div className="flex gap-6 mt-8 lg:hidden z-20">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={prevSlide}
                                className="rounded-full w-12 h-12 border-white/20 bg-white/5 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm transition-all"
                            >
                                <ChevronLeft className="h-6 w-6" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={nextSlide}
                                className="rounded-full w-12 h-12 border-white/20 bg-white/5 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm transition-all"
                            >
                                <ChevronRight className="h-6 w-6" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
